/**
 * AdminDispatchModeration — Owner moderation for Archive Dispatch.
 * Lives at /admin/security/dispatch.
 *
 * Tabs:
 *   Reports  — open / resolved / dismissed moderation queue
 *   Threads  — search ALL threads (admin can never see emails of other
 *              members' counterparts elsewhere, but here we DO show email
 *              because admin has full visibility for moderation/legal)
 *   Broadcast — Owner → many members (creates Dispatch threads)
 *   Stats    — top-line counters
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Flag, Shield, Send, Activity, Search, RefreshCw, ArchiveX,
  EyeOff, Megaphone, CheckCircle2, AlertCircle, Loader2, X,
} from "lucide-react";
import {
  adminDispatchStats, adminDispatchReports, adminDispatchResolveReport,
  adminDispatchListThreads, adminDispatchThread, adminDispatchHideMessage,
  adminDispatchBroadcast,
} from "../../lib/api";

const TABS = [
  { key: "reports",   label: "Reports",   icon: Flag },
  { key: "threads",   label: "Threads",   icon: Activity },
  { key: "broadcast", label: "Broadcast", icon: Megaphone },
  { key: "stats",     label: "Stats",     icon: Shield },
];

export const AdminDispatchModeration = ({ token, userToken }) => {
  const [tab, setTab] = useState("reports");
  return (
    <div data-testid="admin-dispatch-shell">
      <div className="mb-3">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// DISPATCH MODERATION</div>
        <h1 className="font-display text-4xl text-[#E4DFD3]">Archive Dispatch — Owner Console</h1>
        <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
          Reports queue, thread search, broadcast tool. The Owner never edits
          member conversations — only hides, archives or flags. Every action
          is audit-logged.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-[#2D2A26] mb-4">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-dispatch-tab-${t.key}`}
              className={"flex items-center gap-2 px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 " +
                (active ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
              <t.icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "reports"   && <ReportsTab token={token} userToken={userToken} />}
      {tab === "threads"   && <ThreadsTab token={token} userToken={userToken} />}
      {tab === "broadcast" && <BroadcastTab token={token} userToken={userToken} />}
      {tab === "stats"     && <StatsTab token={token} userToken={userToken} />}
    </div>
  );
};

// ---------- Reports ----------------------------------------------------

const ReportsTab = ({ token, userToken }) => {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminDispatchReports(token, { status, limit: 100 }, userToken);
      setRows(r.rows || []);
    } catch { toast.error("Failed to load reports"); }
    finally { setLoading(false); }
  }, [token, userToken, status]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (rid, action) => {
    if (!window.confirm(`Confirm action: ${action.replace("_", " ")}?`)) return;
    try {
      await adminDispatchResolveReport(token, rid, { action }, userToken);
      toast.success(`Report ${action}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div data-testid="admin-dispatch-reports">
      <div className="flex items-center gap-2 mb-3">
        {["open", "resolved", "dismissed", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`reports-filter-${s}`}
            className={"px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.25em] " +
              (status === s ? "border-[#D92D20] text-[#D92D20] bg-[#1a0808]" : "border-[#2D2A26] text-[#A39E93]")}>{s}</button>
        ))}
        <button onClick={load} className="ml-auto text-[#A39E93] hover:text-[#D92D20]" data-testid="reports-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-[#2D2A26] p-10 text-center font-mono text-xs text-[#757065]">No reports in this status.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="border border-[#2D2A26] bg-[#0a0a0a] p-3" data-testid={`report-row-${r.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">{r.category}</span>
                    <span className="font-mono text-[10px] text-[#757065]">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{
                      color: r.status === "open" ? "#D92D20" : r.status === "resolved" ? "#5DD4B3" : "#A39E93"
                    }}>{r.status}</span>
                  </div>
                  <div className="font-mono text-xs text-[#A39E93] mb-1">
                    Reporter: <span className="text-[#E4DFD3]">{r.reporter?.callsign}</span> ({r.reporter?.email})
                  </div>
                  <div className="font-mono text-xs text-[#A39E93] mb-2">
                    Sender: <span className="text-[#E4DFD3]">{r.sender?.callsign || "[unknown]"}</span> ({r.sender?.email || ""})
                  </div>
                  {r.reason && <div className="text-xs text-[#E4DFD3] mb-2 italic">{`"${r.reason}"`}</div>}
                  <div className="border-l-2 border-[#454038] pl-3 py-1 bg-[#0F0F0F]">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#757065] mb-1">MESSAGE</div>
                    <div className="text-sm text-[#E4DFD3]">{r.message?.body || "[hidden]"}</div>
                    <div className="font-mono text-[10px] text-[#757065] mt-1">
                      Status: {r.message?.status} · Reported {r.message?.reported_count}×
                    </div>
                  </div>
                </div>
                {r.status === "open" && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => resolve(r.id, "dismiss")} className="border border-[#454038] hover:border-[#A39E93] text-[#A39E93] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em]" data-testid={`report-dismiss-${r.id}`}>Dismiss</button>
                    <button onClick={() => resolve(r.id, "warn_sender")} className="border border-[#E0A526]/40 hover:bg-[#E0A526]/15 text-[#E0A526] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em]" data-testid={`report-warn-${r.id}`}>Warn</button>
                    <button onClick={() => resolve(r.id, "hide_message")} className="border border-[#9FA8D4]/40 hover:bg-[#9FA8D4]/15 text-[#9FA8D4] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em]" data-testid={`report-hide-${r.id}`}>Hide Msg</button>
                    <button onClick={() => resolve(r.id, "archive_thread")} className="border border-[#9FA8D4]/40 hover:bg-[#9FA8D4]/15 text-[#9FA8D4] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em]" data-testid={`report-archive-${r.id}`}>Archive Thread</button>
                    <button onClick={() => resolve(r.id, "block_sender_globally")} className="border border-[#D92D20]/40 hover:bg-[#D92D20]/15 text-[#D92D20] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em]" data-testid={`report-block-${r.id}`}>Block Globally</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Threads ----------------------------------------------------

const ThreadsTab = ({ token, userToken }) => {
  const [rows, setRows] = useState([]);
  const [callsign, setCallsign] = useState("");
  const [flagged, setFlagged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openThread, setOpenThread] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminDispatchListThreads(token, {
        callsign: callsign || undefined, flagged: flagged || undefined,
        limit: 100,
      }, userToken);
      setRows(r.rows || []);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, [token, userToken, callsign, flagged]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-dispatch-threads">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-2 border border-[#2D2A26] bg-[#0F0F0F] px-3 py-1.5">
          <Search size={12} className="text-[#757065]" />
          <input value={callsign} onChange={(e) => setCallsign(e.target.value)} placeholder="Search by participant callsign" data-testid="threads-search"
            className="bg-transparent outline-none text-xs text-[#E4DFD3] placeholder:text-[#757065] w-72" />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={flagged} onChange={(e) => setFlagged(e.target.checked)} data-testid="threads-flagged-only" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39E93]">Flagged only</span>
        </label>
        <button onClick={load} className="ml-auto text-[#A39E93] hover:text-[#D92D20]" data-testid="threads-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="border border-[#2D2A26] bg-[#0a0a0a] divide-y divide-[#2D2A26]/70">
        {rows.length === 0 && !loading ? (
          <div className="px-4 py-10 text-center font-mono text-xs text-[#757065]">No threads match.</div>
        ) : rows.map((t) => (
          <button key={t.id} onClick={() => setOpenThread(t)} data-testid={`admin-thread-row-${t.id}`}
            className="w-full text-left px-3 py-2 hover:bg-[#131313] flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {t.participants.map((p, i) => (
                  <span key={i} className="font-display text-base text-[#E4DFD3]">{p.callsign}</span>
                )).reduce((acc, x, i) => acc.length ? [...acc, <span key={`s${i}`} className="text-[#757065]">↔</span>, x] : [x], [])}
                {t.admin_flagged && <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#D92D20]">FLAGGED</span>}
                <span className="ml-auto font-mono text-[10px] text-[#757065]">{t.message_count} msgs · {t.last_message_at ? new Date(t.last_message_at).toLocaleDateString() : ""}</span>
              </div>
              {t.last_message_preview && (
                <div className="font-mono text-xs text-[#A39E93] truncate mt-1">{t.last_message_preview}</div>
              )}
            </div>
          </button>
        ))}
      </div>

      {openThread && (
        <AdminThreadModal token={token} userToken={userToken} tid={openThread.id} onClose={() => { setOpenThread(null); load(); }} />
      )}
    </div>
  );
};

const AdminThreadModal = ({ token, userToken, tid, onClose }) => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await adminDispatchThread(token, tid, userToken); setData(r); }
    catch { toast.error("Failed"); }
  }, [token, userToken, tid]);
  useEffect(() => { load(); }, [load]);
  const hide = async (mid) => {
    if (!window.confirm("Hide this message globally?")) return;
    try { await adminDispatchHideMessage(token, mid, userToken); toast.success("Hidden"); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-thread-modal">
      <div className="w-full max-w-3xl border border-[#D92D20]/40 bg-[#0F0F0F] my-8">
        <div className="flex items-center justify-between border-b border-[#2D2A26] px-4 py-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// THREAD INSPECTION</div>
          <button onClick={onClose} className="text-[#A39E93] hover:text-[#D92D20]"><X size={14} /></button>
        </div>
        <div className="p-4">
          {!data ? <div className="text-xs text-[#757065]">Loading…</div> : (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto">
              {(data.messages || []).map((m) => (
                <div key={m.id} className="border border-[#2D2A26] bg-[#0a0a0a] p-2" data-testid={`admin-msg-${m.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-display text-sm text-[#E4DFD3]">{m.sender?.callsign}</span>
                    <span className="font-mono text-[10px] text-[#757065]">({m.sender?.email})</span>
                    <span className="font-mono text-[10px] text-[#757065]">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span>
                    {m.status === "hidden" && <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#D92D20]">HIDDEN</span>}
                    {m.reported_count > 0 && <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#E0A526]">⚑ {m.reported_count}</span>}
                    {m.status === "visible" && (
                      <button onClick={() => hide(m.id)} className="ml-auto border border-[#D92D20]/40 hover:bg-[#D92D20]/15 text-[#D92D20] px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em]" data-testid={`admin-msg-hide-${m.id}`}>
                        Hide Globally
                      </button>
                    )}
                  </div>
                  <div className="text-sm text-[#E4DFD3]">{m.body}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Broadcast --------------------------------------------------

const BroadcastTab = ({ token, userToken }) => {
  const [subject, setSubject] = useState("Archive Broadcast");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all_discoverable");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!body.trim()) return toast.error("Body required");
    if (!window.confirm(`Broadcast to "${audience}"?`)) return;
    setBusy(true);
    try {
      const r = await adminDispatchBroadcast(token, {
        subject, body, audience, confirm: "APPLY",
      }, userToken);
      setResult(r);
      toast.success(`Sent to ${r.sent} of ${r.audience_total}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-3" data-testid="admin-dispatch-broadcast">
      <div className="lg:col-span-2 border border-[#2D2A26] bg-[#0a0a0a] p-4 space-y-3">
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="broadcast-subject"
          className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3]" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} placeholder="Broadcast body — appears as a Dispatch thread for each recipient" data-testid="broadcast-body"
          className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] resize-y" />
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="broadcast-confirm" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E4DFD3]">I have reviewed the audience scope</span>
        </label>
        <button onClick={send} disabled={busy || !confirmed} data-testid="broadcast-send"
          className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Megaphone size={12} />} Send Broadcast
        </button>
      </div>
      <div className="border border-[#2D2A26] bg-[#0a0a0a] p-4 space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// AUDIENCE</div>
        {[
          { key: "all_discoverable", label: "All discoverable members" },
          { key: "all",              label: "EVERY member (including hidden)" },
          { key: "founding",         label: "Founding members only" },
          { key: "members_tier",     label: "Tier = member" },
          { key: "subscribers_tier", label: "Tier = subscriber" },
        ].map((o) => (
          <label key={o.key} className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={audience === o.key} onChange={() => setAudience(o.key)} data-testid={`broadcast-aud-${o.key}`} />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E4DFD3]">{o.label}</span>
          </label>
        ))}
        {result && (
          <div className="border border-[#5DD4B3]/40 bg-[#0a1a13] p-2 mt-3 font-mono text-[10px] text-[#5DD4B3]" data-testid="broadcast-result">
            Sent: {result.sent} · Audience: {result.audience_total} · Campaign: {result.campaign_id}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Stats ------------------------------------------------------

const Stat = ({ label, value, color }) => (
  <div className="border border-[#2D2A26] bg-[#0a0a0a] p-4">
    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">{label}</div>
    <div className="font-display text-3xl" style={{ color }}>{value}</div>
  </div>
);

const StatsTab = ({ token, userToken }) => {
  const [s, setS] = useState(null);
  useEffect(() => {
    adminDispatchStats(token, userToken).then(setS).catch(() => toast.error("Failed"));
  }, [token, userToken]);
  if (!s) return <div className="font-mono text-xs text-[#757065]">Loading…</div>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-dispatch-stats">
      <Stat label="Threads" value={s.threads_total} color="#E4DFD3" />
      <Stat label="Messages" value={s.messages_total} color="#E4DFD3" />
      <Stat label="24h Volume" value={s.messages_24h} color="#5DD4B3" />
      <Stat label="7d Volume" value={s.messages_7d} color="#5DD4B3" />
      <Stat label="Open Reports" value={s.reports_open} color="#D92D20" />
      <Stat label="Total Reports" value={s.reports_total} color="#A39E93" />
      <Stat label="Blocks Active" value={s.blocks_total} color="#9FA8D4" />
      <Stat label="Discoverable" value={s.discoverable_members} color="#E0A526" />
    </div>
  );
};

export default AdminDispatchModeration;
